import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase/client";
import { insertLoginLog, updateLastActive, purgeOldLoginLogs, cleanupStaleInviteAndMigrateAccess } from "../lib/supabase/queries/users.queries";
import { fetchUserBrandAccess, fetchUserOrgRole, fetchOrgSettings } from "../lib/supabase/queries/org.queries";

const AuthContext = createContext(null);

const ALLOWED_DOMAINS = ["@khave.com", "@haliteglobal.com", "@khave.co.uk", "@haliteinc.com"];

function isAllowedEmail(email, domainList) {
  if (!email) return false;
  const domains = domainList && domainList.length > 0 ? domainList : ALLOWED_DOMAINS;
  return domains.some((d) => email.toLowerCase().endsWith(d));
}

async function fetchIp() {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const { ip } = await res.json();
    return ip;
  } catch {
    return null;
  }
}

async function logLogin(userId, email) {
  const ip = await fetchIp();
  await insertLoginLog(userId, email, ip, navigator.userAgent);
  await updateLastActive(userId);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 60);
  await purgeOldLoginLogs(cutoff.toISOString());
}

export function AuthProvider({ children }) {
  const [session, setSession]         = useState(null);
  const [profile, setProfile]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [domainError, setDomainError] = useState(null);

  // S65 — org/brand context
  const [orgId, setOrgId]             = useState(null);
  const [orgRole, setOrgRole]         = useState(null);
  const [orgName, setOrgName]         = useState(null);
  const [brandId, setBrandId]         = useState(null);
  const [brandRole, setBrandRole]     = useState(null);
  const [brandName, setBrandName]     = useState(null);
  const [brandIconUrl, setBrandIconUrl] = useState(null);
  const [authMethods, setAuthMethods] = useState({ google: true, microsoft: false, apple: false, magicLink: false });

  async function syncProfile(supabaseSession, event) {
    if (!supabaseSession) {
      setProfile(null);
      setOrgId(null); setOrgRole(null); setOrgName(null);
      setBrandId(null); setBrandRole(null); setBrandName(null);
      return;
    }

    const { user } = supabaseSession;

    // ── Domain restriction (use hardcoded fallback, fetch from DB later) ──
    if (!isAllowedEmail(user.email, null)) {
      setDomainError(
        "Access is restricted to @khave.com, @haliteglobal.com, @khave.co.uk, and @haliteinc.com accounts."
      );
      await supabase.auth.signOut();
      return;
    }

    setDomainError(null);

    const ownerEmail = process.env.REACT_APP_PLATFORM_OWNER_EMAIL?.toLowerCase().trim();
    const isPlatformOwner = !!ownerEmail && user.email.toLowerCase() === ownerEmail;

    let data;
    try {
      const { data: profileData, error } = await supabase.rpc("bootstrap_profile", {
        p_id:         user.id,
        p_email:      user.email,
        p_full_name:  user.user_metadata?.full_name  || user.email,
        p_avatar_url: user.user_metadata?.avatar_url || null,
        p_is_owner:   isPlatformOwner,
      });
      if (error) throw error;
      data = profileData;
    } catch (err) {
      console.error("Profile sync error:", err.message);
      return;
    }

    if (data) {
      setProfile(data);

      // ── Resolve org + brand context (Phase 0 fix + S65) ──
      try {
        const [brandAccessRows, orgRoleData] = await Promise.all([
          fetchUserBrandAccess(user.id),
          fetchUserOrgRole(user.id),
        ]);

        // Brand context — use first brand access row
        if (brandAccessRows.length > 0) {
          const primary = brandAccessRows[0];
          setBrandId(primary.brand_id);
          setBrandRole(primary.role);
          setBrandName(primary.brands?.name || null);
          setBrandIconUrl(primary.brands?.icon_url || null);
        } else if (data.brand_id) {
          // Fallback to profile.brand_id from bootstrap_profile
          setBrandId(data.brand_id);
        }

        // Org context
        if (orgRoleData) {
          setOrgId(orgRoleData.org_id);
          setOrgRole(orgRoleData.role);
          setOrgName(orgRoleData.organisations?.name || null);

          // Fetch org_settings for auth methods + domain whitelist
          try {
            const settings = await fetchOrgSettings(orgRoleData.org_id);
            if (settings) {
              setAuthMethods({
                google: settings.google_auth_enabled,
                microsoft: settings.microsoft_auth_enabled,
                apple: settings.apple_auth_enabled,
                magicLink: settings.magic_link_enabled,
              });
            }
          } catch {
            // org_settings may not exist yet — use defaults
          }
        }
      } catch (err) {
        // Non-fatal — brand_id from profile is the fallback
        if (data.brand_id) setBrandId(data.brand_id);
      }

      cleanupStaleInviteAndMigrateAccess(user.email, user.id).catch(() => {});
      if (event === "SIGNED_IN") {
        logLogin(user.id, user.email);
      }
    }
  }

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
      syncProfile(nextSession, event);
    });

    return () => listener.subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime: re-sync profile on user row update
  useEffect(() => {
    if (!profile?.id) return;
    const channel = supabase
      .channel(`user-role-${profile.id}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "users", filter: `id=eq.${profile.id}` },
        (payload) => { setProfile(payload.new); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime: dept access changes
  const [deptAccessVersion, setDeptAccessVersion] = useState(0);
  useEffect(() => {
    if (!profile?.id) return;
    const channel = supabase
      .channel(`dept-access-${profile.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "user_section_access", filter: `user_id=eq.${profile.id}` },
        () => { setDeptAccessVersion(v => v + 1); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [profile?.id]);

  const signOut = () => supabase.auth.signOut();

  const value = {
    session,
    user:        session?.user ?? null,
    profile,
    loading,
    signOut,
    domainError,
    isAdmin:    profile?.role === "admin",
    isEditor:   profile?.role === "editor" || profile?.role === "admin",
    isLoggedIn: !!session,
    deptAccessVersion,
    // S65 — org/brand context
    orgId,
    orgRole,
    orgName,
    brandId,
    brandRole,
    brandName,
    brandIconUrl,
    authMethods,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
